// 统一 API 工具 - 使用完整 URL，确保网页版和手机版都能正常工作
// 核心特性：
// 1. 所有请求添加防缓存头，防止运营商/CDN缓存
// 2. 时间戳 query 参数防止 HTTP 缓存
// 3. 版本号检测机制 - 检测到新版本时提示用户刷新

import { APP_VERSION } from '../utils/version';

// 统一使用完整 URL（CORS 已在 Cloudflare 配置）
// 这样网页版和 APK 版都使用同一个 API，数据完全同步
const API_BASE = 'https://nzx-5o4.pages.dev/api';

/**
 * 响应头版本检测已移除
 * 统一使用 AppUpdateManager 组件进行版本检测
 * 避免与原生更新机制冲突
 */
function checkAppVersion(response: Response) {
  // 空实现 - 版本检测由 AppUpdateManager 统一处理
}

/**
 * 统一的请求工具 - 使用 XMLHttpRequest 而非 fetch
 * - Android WebView 中 fetch 的 CORS 预检存在兼容性问题
 * - XMLHttpRequest 对 CORS 处理更宽松
 */
export async function apiFetch<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${path.startsWith('/') ? path : '/' + path}`;

  const headers: Record<string, string> = {};
  if (options.headers) {
    const hs = options.headers as Record<string, string>;
    for (const k of Object.keys(hs)) {
      headers[k] = hs[k];
    }
  }

  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const finalUrl = addCacheBuster(url);
  const timeout = 30000;

  console.log(`[API请求] ${method} ${finalUrl}`);

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, finalUrl, true);
    xhr.timeout = timeout;

    // 设置 headers
    for (const [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }

    xhr.onload = () => {
      console.log(`[API响应] ${xhr.status} ${finalUrl}`);
      if (xhr.status >= 200 && xhr.status < 300) {
        const contentType = xhr.getResponseHeader('content-type') || '';
        let data: any;
        try {
          data = contentType.includes('application/json')
            ? JSON.parse(xhr.responseText)
            : xhr.responseText;
        } catch {
          data = xhr.responseText;
        }
        console.log(`[API成功] 返回:`, typeof data === 'object' && Array.isArray(data) ? `${data.length} 条` : '成功');
        resolve(data as T);
      } else {
        let msg = `请求失败 (${xhr.status})`;
        try {
          const err = JSON.parse(xhr.responseText);
          if (err.error) msg = err.error;
        } catch {
          // 忽略解析失败
        }
        console.error(`[API错误] ${xhr.status}: ${msg}`);
        const error = new Error(msg);
        (error as any).status = xhr.status;
        reject(error);
      }
    };

    xhr.onerror = () => {
      const isAndroid = navigator.userAgent.includes('Android');
      const errorMsg = isAndroid
        ? `网络请求失败（XMLHttpRequest）。请检查：\n1. 网络连接是否正常\n2. 手机是否能访问 ${API_BASE}\n3. 系统时间是否正确`
        : '网络请求失败，请检查网络连接';
      console.error(`[API请求失败] ${method} ${url}: XMLHttpRequest onerror`);
      reject(new Error(errorMsg));
    };

    xhr.ontimeout = () => {
      console.error(`[API超时] ${finalUrl}: 请求超过 ${timeout}ms`);
      reject(new Error('请求超时，请检查网络连接'));
    };

    // 发送请求
    let body: any = null;
    if (options.body) {
      if (typeof options.body === 'string') {
        body = options.body;
      } else if (options.body instanceof FormData) {
        body = options.body;
      } else {
        body = JSON.stringify(options.body);
      }
    }
    xhr.send(body);
  });
}

/**
 * 添加防缓存的时间戳参数
 * /api/videos -> /api/videos?_t=1234567890
 */
function addCacheBuster(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_t=${Date.now()}`;
}

// 便捷方法
export const api = {
  get: <T = any>(path: string, options: RequestInit = {}) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),

  post: <T = any>(path: string, body?: any, options: RequestInit = {}) =>
    apiFetch<T>(path, {
      ...options,
      method: 'POST',
      body: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
    }),

  put: <T = any>(path: string, body?: any, options: RequestInit = {}) =>
    apiFetch<T>(path, {
      ...options,
      method: 'PUT',
      body: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
    }),

  delete: <T = any>(path: string, options: RequestInit = {}) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),

  // multipart/form-data 上传专用（不自动设置 Content-Type）
  upload: <T = any>(path: string, formData: FormData, options: RequestInit = {}) =>
    apiFetch<T>(path, {
      ...options,
      method: 'POST',
      body: formData,
      // 让浏览器自动设置 multipart boundary
      headers: {
        ...(options.headers as Record<string, string> || {}),
      } as any,
    }),
};

/**
 * 强制清除缓存并刷新页面（用户手动触发）
 */
export function forceRefresh() {
  // 清除 Service Worker 缓存（如果有）
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => caches.delete(name));
    });
  }
  // 强制刷新
  window.location.reload();
}
